import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from 'components';
import PopupWrapper from 'components/Popups/Wrapper';
import { CloseIconBigger } from 'static';
import { getBrandIconComponent } from 'utils/stripePaymentMethod';

import {
  AddCardCallout,
  AddCardCalloutBody,
  AddCardCalloutTitle,
  AddCardHero,
  AddCardIconRing,
  CloseButton,
  ContentArea,
  CycleNote,
  LoadingOverlay,
  LoadingText,
  ModalActions,
  ModalBody,
  ModalBodyBold,
  ModalBtnPrimary,
  ModalBtnSecondary,
  ModalError,
  ModalPanel,
  ModalTitle,
  ModeArrow,
  ModeCard,
  ModeCardLabel,
  ModeCardValue,
  ModeIconBadge,
  ModeIconGraphic,
  ModeSwitchRow,
  LoaderSlot,
} from './SwitchBillingModeModal.styled';

export type SwitchBillingModeStep = 'confirm' | 'add-card';

type SwitchBillingModeModalProps = {
  isOpen: boolean;
  isInvoiceBilling: boolean;
  step: SwitchBillingModeStep;
  submitting: boolean;
  redirecting: boolean;
  error: string | null;
  cardBrand?: string | null;
  cardSummary?: string | null;
  onClose: () => void;
  onConfirm: () => void;
  onGoAddCard: () => void;
};

const IconInvoice = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
  </svg>
);

const IconCard = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
  </svg>
);

const IconArrow = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
  </svg>
);

const IconCalendar = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
  </svg>
);

const IconLock = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
);

const IconShield = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
  </svg>
);

const SwitchBillingModeModal = ({
  isOpen,
  isInvoiceBilling,
  step,
  submitting,
  redirecting,
  error,
  cardBrand,
  cardSummary,
  onClose,
  onConfirm,
  onGoAddCard,
}: SwitchBillingModeModalProps) => {
  const { t } = useTranslation();
  const busy = submitting || redirecting;

  const copy = useMemo(() => {
    if (isInvoiceBilling) {
      return {
        title:
          step === 'add-card'
            ? t('subscriptions.profile.payment.switchBillingAddCardTitle')
            : t('subscriptions.profile.payment.switchToCardTitle'),
        body: t('subscriptions.profile.payment.switchToCardBody'),
        bold: t('subscriptions.profile.payment.switchToCardBodyBold'),
        confirm: t('subscriptions.profile.payment.switchToCardConfirm'),
        fromLabel: t('subscriptions.profile.payment.switchBillingInvoiceLabel'),
        toLabel: t('subscriptions.profile.payment.switchBillingCardLabel'),
      };
    }
    return {
      title: t('subscriptions.profile.payment.switchToInvoiceTitle'),
      body: t('subscriptions.profile.payment.switchToInvoiceBody'),
      bold: t('subscriptions.profile.payment.switchToInvoiceBodyBold'),
      confirm: t('subscriptions.profile.payment.switchToInvoiceConfirm'),
      fromLabel: t('subscriptions.profile.payment.switchBillingCardLabel'),
      toLabel: t('subscriptions.profile.payment.switchBillingInvoiceLabel'),
    };
  }, [isInvoiceBilling, step, t]);

  const cardSideLabel =
    cardSummary || (isInvoiceBilling ? copy.toLabel : copy.fromLabel);

  const renderModeIcon = (
    kind: 'invoice' | 'card' | 'brand',
    emphasis?: boolean,
  ) => {
    if (kind === 'brand' && cardBrand) {
      const BrandIcon = getBrandIconComponent(cardBrand);
      return (
        <ModeIconBadge $emphasis={emphasis} aria-hidden>
          <ModeIconGraphic>
            <BrandIcon />
          </ModeIconGraphic>
        </ModeIconBadge>
      );
    }

    return (
      <ModeIconBadge $emphasis={emphasis} aria-hidden>
        {kind === 'invoice' ? <IconInvoice /> : <IconCard />}
      </ModeIconBadge>
    );
  };

  const fromIconKind = isInvoiceBilling
    ? 'invoice'
    : cardBrand
    ? 'brand'
    : 'card';
  const toIconKind = isInvoiceBilling
    ? cardBrand
      ? 'brand'
      : 'card'
    : 'invoice';

  return (
    <PopupWrapper isOpen={isOpen}>
      <ModalPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="switch-billing-title"
      >
        <CloseButton
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label={t('common.cancel')}
        >
          <CloseIconBigger />
        </CloseButton>

        <ContentArea>
          {step === 'confirm' ? (
            <>
              <ModalTitle id="switch-billing-title">{copy.title}</ModalTitle>
              <ModalBody>{copy.body}</ModalBody>
              <ModalBodyBold>{copy.bold}</ModalBodyBold>

              <ModeSwitchRow aria-hidden>
                <ModeCard>
                  {renderModeIcon(fromIconKind)}
                  <ModeCardLabel>
                    {t('subscriptions.profile.payment.switchBillingFromLabel')}
                  </ModeCardLabel>
                  <ModeCardValue>
                    {isInvoiceBilling ? copy.fromLabel : cardSideLabel}
                  </ModeCardValue>
                </ModeCard>
                <ModeArrow>
                  <IconArrow />
                </ModeArrow>
                <ModeCard $highlight>
                  {renderModeIcon(toIconKind, true)}
                  <ModeCardLabel>
                    {t('subscriptions.profile.payment.switchBillingToLabel')}
                  </ModeCardLabel>
                  <ModeCardValue>
                    {isInvoiceBilling ? cardSideLabel : copy.toLabel}
                  </ModeCardValue>
                </ModeCard>
              </ModeSwitchRow>

              <CycleNote>
                <IconCalendar />
                <span>
                  {t(
                    'subscriptions.profile.payment.switchBillingNextCycleNote',
                  )}
                </span>
              </CycleNote>
            </>
          ) : (
            <>
              <AddCardHero>
                <AddCardIconRing>
                  <IconLock />
                </AddCardIconRing>
                <ModalTitle id="switch-billing-title">{copy.title}</ModalTitle>
              </AddCardHero>

              <AddCardCallout>
                <AddCardCalloutTitle>
                  <IconShield />
                  {t(
                    'subscriptions.profile.payment.switchBillingAddCardRequired',
                  )}
                </AddCardCalloutTitle>
                <AddCardCalloutBody>
                  {t('subscriptions.profile.payment.switchBillingAddCardBody')}
                </AddCardCalloutBody>
              </AddCardCallout>
            </>
          )}

          {error ? <ModalError role="alert">{error}</ModalError> : null}

          <ModalActions>
            <ModalBtnSecondary type="button" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </ModalBtnSecondary>
            {step === 'confirm' ? (
              <ModalBtnPrimary
                type="button"
                onClick={onConfirm}
                disabled={busy}
              >
                {copy.confirm}
              </ModalBtnPrimary>
            ) : (
              <ModalBtnPrimary
                type="button"
                onClick={onGoAddCard}
                disabled={busy}
              >
                {t('subscriptions.profile.payment.switchBillingAddCardGo')}
              </ModalBtnPrimary>
            )}
          </ModalActions>

          {busy ? (
            <LoadingOverlay aria-live="polite" aria-busy="true">
              <LoaderSlot>
                <Loader size="56px" type={null} />
              </LoaderSlot>
              <LoadingText>
                {redirecting
                  ? t('subscriptions.profile.payment.switchBillingRedirecting')
                  : t('subscriptions.profile.payment.switchBillingProcessing')}
              </LoadingText>
            </LoadingOverlay>
          ) : null}
        </ContentArea>
      </ModalPanel>
    </PopupWrapper>
  );
};

export default SwitchBillingModeModal;
